import React from 'react';
import { useNavigate } from 'react-router-dom';

const License = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-purple-400 hover:text-purple-300 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </button>
          <h1 className="text-4xl font-bold text-white mb-2">License Agreement</h1>
          <p className="text-gray-400">Last updated: October 15, 2025</p>
        </div>

        {/* Content */}
        <div className="reaction-card space-y-6 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-3">1. License Grant</h2>
            <p>
              Dexreaction grants you a limited, non-exclusive, non-transferable, revocable license to access and use our services 
              strictly in accordance with these terms. This license does not include any right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Resell or commercially exploit our services</li>
              <li>Modify, copy, or distribute our content</li>
              <li>Use our services for unauthorized purposes</li>
              <li>Reverse engineer or decompile our software</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">2. Intellectual Property Rights</h2>
            <p className="mb-2">
              All content, features, and functionality of Dexreaction are owned by us or our licensors and are protected by:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Copyright laws</li>
              <li>Trademark laws</li>
              <li>Patent laws</li>
              <li>Trade secret laws</li>
              <li>Other intellectual property rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">3. Trademarks</h2>
            <p>
              "Dexreaction" and associated logos, designs, and marks are trademarks of Dexreaction. You may not use these 
              trademarks without our prior written permission. All other trademarks, service marks, and logos used on 
              our platform are the property of their respective owners.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">4. User-Generated Content</h2>
            <p className="mb-2">
              By submitting content to our platform (support tickets, feedback, etc.), you grant us:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>A worldwide, royalty-free license to use, reproduce, and display such content</li>
              <li>The right to modify and adapt content for service improvement</li>
              <li>The right to use content for promotional purposes</li>
            </ul>
            <p className="mt-2">
              You retain ownership of your content but warrant that you have the right to grant us these licenses.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">5. Third-Party Services</h2>
            <p className="mb-2">
              Our services integrate with third-party platforms:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong className="text-white">DexScreener:</strong> Reaction delivery platform</li>
              <li><strong className="text-white">Solana Blockchain:</strong> Payment processing</li>
            </ul>
            <p className="mt-2">
              Use of these services is subject to their respective terms and licenses. We are not responsible for 
              third-party service availability or terms. Additional third-party services may be utilized for 
              order processing and fulfillment.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">6. Open Source Components</h2>
            <p className="mb-2">
              Our platform uses various open-source libraries and components:
            </p>
            <div className="bg-slate-800/50 p-4 rounded-lg mt-2 space-y-2">
              <div>
                <p className="font-semibold text-white">React</p>
                <p className="text-sm">Licensed under MIT License</p>
              </div>
              <div>
                <p className="font-semibold text-white">FastAPI</p>
                <p className="text-sm">Licensed under MIT License</p>
              </div>
              <div>
                <p className="font-semibold text-white">Tailwind CSS</p>
                <p className="text-sm">Licensed under MIT License</p>
              </div>
              <div>
                <p className="font-semibold text-white">MongoDB</p>
                <p className="text-sm">Licensed under Server Side Public License (SSPL)</p>
              </div>
            </div>
            <p className="mt-2 text-sm">
              Full license texts for these components are available in our source code repository.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">7. Restrictions</h2>
            <p className="mb-2">You may not:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Remove, alter, or obscure any copyright, trademark, or proprietary notices</li>
              <li>Use our services to develop competing products or services</li>
              <li>Access our services through automated means without permission</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Copy, modify, or create derivative works of our services</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">8. License Termination</h2>
            <p>
              This license is effective until terminated. We may terminate or suspend your license immediately, without 
              notice, for any breach of these terms. Upon termination, you must cease all use of our services and 
              destroy any downloaded materials.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">9. Attribution Requirements</h2>
            <p>
              If you are permitted to use our branding or content, you must:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
              <li>Provide appropriate credit to Dexreaction</li>
              <li>Include a link to our website</li>
              <li>Indicate if changes were made</li>
              <li>Not suggest endorsement without permission</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">10. Disclaimer of Warranties</h2>
            <p>
              Our services are provided "as is" and "as available" without warranties of any kind, express or implied. 
              We do not warrant that services will be uninterrupted, error-free, or secure. Use of our services is at 
              your own risk.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">11. Updates and Modifications</h2>
            <p>
              We reserve the right to update, modify, or discontinue any aspect of our services at any time. We may 
              update this license agreement periodically. Continued use after changes constitutes acceptance of the 
              modified license.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-3">12. Contact Information</h2>
            <p>
              For licensing inquiries or permissions, contact us at:
            </p>
            <ul className="list-none space-y-1 ml-4 mt-2">
              <li>Email: legal@dexreaction.com</li>
              <li>Support: Through our ticket system</li>
            </ul>
          </section>

          <div className="mt-8 pt-6 border-t border-purple-500/20">
            <p className="text-sm text-gray-400">
              This license agreement governs your use of Dexreaction services. By using our services, you acknowledge 
              that you have read, understood, and agree to be bound by this license.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default License;
